import { resetAuthState, setAuthAction, setIsLinkUserExist, updateSignUpData } from 'src/redux/auth/reducer';
import { AuthProvidersEnum, ProvderToProviderIdMatch } from '..';
import {
  CreateUserWithEmailAndPasswordOptions,
  FirebaseAuthentication,
  LinkWithEmailAndPasswordOptions,
  SendPasswordResetEmailOptions,
  SignInWithEmailAndPasswordOptions,
  UpdateEmailOptions,
  User,
} from '@capacitor-firebase/authentication';
import { useDispatch, useSelector } from 'react-redux';
import { createUserProfile, linkUserProfile, loadUserThunk, updateUserProfile } from 'src/redux/user/thunk';
import { useCallback, useContext } from 'react';
import { PrivateRoutes } from 'src/routes/navigation';
import { useAnalytics } from 'src/shared/hooks/useAnalytics';
import { useHistory } from 'react-router';
import {
  newOnBoardingAnswersSelector,
  onBoardingUserIdSelector,
  newOnBoardingDataSelector,
  redirectToSelector,
} from 'src/redux/auth/selector';
import { useUser } from './useUser';
import { useLog } from './useLog';
import { AuthContext, UserType } from '@mindly/ui-components';
import { initChatRooms } from 'src/redux/chat/thunk';
import { loadConsultationsThunk } from 'src/redux/consultations/action';
import { fetchSubscriptions } from 'src/redux/subscriptions/thunk';
import { OAuthProvider } from 'firebase/auth';
import { checkIsEmailExist, mintCustomTokenForProviderOwnerEndpoint } from 'src/api/auth/auth';
import { removeUserApi, updateUserEmail } from 'src/api/user';
import { logout as logoutAction } from 'src/redux/user/reducer';
import { updateUserMetadataAnalyticsParams } from 'src/api/user-metadata';
import { isPlatform } from '@ionic/react';
import { clearSavedFilters, clearSavedSort } from '../utils/filters';
import i18n from 'src/i18n';
import useIntercomPlugin from './useIntercomPlugin';

const useAuthContextWrapper = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContextWrapper must be used within an AuthContext');
  }
  return context;
};

export const useAuthFlow = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { trackPredefinedEvent, setUserProperties, reset } = useAnalytics();
  const { clearUser } = useIntercomPlugin();

  const { redirectToOnboarding } = useUser();
  const { logError } = useLog();
  const {
    signInWithApple,
    signInWithGoogle,
    linkWithGoogle,
    linkWithApple,
    signInAnonymously,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signOut,
    linkWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    changePassword,
    isAuthLoading,
    isAuthReady,
    authUser,
  } = useAuthContextWrapper();

  const redirectTo = useSelector(redirectToSelector);
  const onBoardingUserId = useSelector(onBoardingUserIdSelector);
  const newOnBoardingAnswers = useSelector(newOnBoardingAnswersSelector);
  const newOnBoardingData = useSelector(newOnBoardingDataSelector);

  /**
   * LOCAL ERROR FILTERING - Auth/Validation errors that shouldn't go to Sentry
   *
   * This implementation filters auth errors at the useAuthFlow level without modifying
   * the core logging infrastructure. It identifies expected user errors (wrong password,
   * user cancellation, validation failures) and prevents them from being sent to Sentry
   * while still allowing genuine logic errors to be logged.
   *
   * Benefits:
   * - Reduces Sentry noise by filtering out expected user errors
   * - Maintains existing logging infrastructure unchanged
   * - Provides clear separation between user errors and logic errors
   * - Logs user errors locally for debugging while sending logic errors to Sentry
   */
  const isAuthUserError = useCallback((error: any): boolean => {
    const errorCode = error?.code || error?.error_code || error?.type || error?.toString();
    const errorMessage = error?.message || error?.error || error?.errorMessage || error?.toString();

    // Firebase auth user errors that are expected
    const authUserErrorCodes = [
      'auth/wrong-password',
      'auth/user-not-found',
      'auth/email-already-in-use',
      'auth/weak-password',
      'auth/invalid-email',
      'auth/invalid-credential',
      'auth/user-disabled',
      'auth/expired-action-code',
      'auth/invalid-action-code',
      'auth/requires-recent-login',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
      'auth/popup-blocked',
    ];
    // Check for auth error codes
    if (errorCode && authUserErrorCodes.includes(errorCode)) {
      return true;
    }

    // Check for user cancellation patterns
    const cancellationPatterns = [
      /user.*cancel/i,
      /popup.*closed/i,
      /sign.*in.*cancel/i,
      /The user canceled the sign-in/i,
      /1001/i, // Apple cancellation
      /12501/i, // Google cancellation
    ];
    if (cancellationPatterns.some((pattern) => pattern.test(errorMessage))) {
      return true;
    }

    // Check for validation error patterns
    const validationPatterns = [
      /validation/i,
      /invalid.*format/i,
      /field.*required/i,
      /email.*invalid/i,
      /password.*weak/i,
    ];
    if (validationPatterns.some((pattern) => pattern.test(errorMessage))) {
      return true;
    }

    return false;
  }, []);

  const handleAuthError = useCallback(
    (error: any, context: { action: string; provider?: string; [key: string]: any }) => {
      if (isAuthUserError(error)) {
        // This is an expected user error - don't send to Sentry, just log locally for debugging
        import.meta.env.DEV && console.warn(`[AUTH] User error in ${context.action}:`, error, context);
        return false; // Indicates this was a user error
      } else {
        // This is a potential logic error - send to Sentry for investigation
        import.meta.env.DEV && console.error(`[AUTH] Logic error in ${context.action}:`, error, context);
        logError(error, context);
        return true; // Indicates this was a logic error
      }
    },
    [isAuthUserError, logError]
  );

  /**
   * HELPER FUNCTIONS
   */
  const checkIfErrorIsUserAlreadyExist = useCallback((error: any) => {
    /**
     * This error is thrown for google login in web and ios
     */
    const credentialAlreadyInUse = 'auth/credential-already-in-use';
    /**
     * This error is thrown for apple login in web and ios
     */
    const emailAlreadyInUse = 'auth/email-already-in-use';
    /**
     * This error is thrown for google login in android
     */
    const credentialAlreadyInUseAndroid = 'This credential is already associated with a different user account';

    const errorCode = error?.code;
    const stringError = error?.toString();

    return (
      // error codes check(because error code in not a part of the string error in native devices)
      errorCode?.includes(credentialAlreadyInUse) ||
      errorCode?.includes(emailAlreadyInUse) ||
      // string error check (mostly for web and android devices)
      stringError.includes(credentialAlreadyInUse) ||
      stringError.includes(emailAlreadyInUse) ||
      stringError.includes(credentialAlreadyInUseAndroid)
    );
  }, []);

  const handleUserAlreadyExistWithDifferentCredentials = useCallback(
    async (error: any) => {
      try {
        if (!isPlatform('hybrid')) {
          const credential = OAuthProvider.credentialFromError(error);

          const {
            data: { token, anonymous_user_id },
          } = await mintCustomTokenForProviderOwnerEndpoint(credential?.providerId ?? '', credential?.idToken ?? '');

          const signInResult = await signInWithCustomToken(token);
          setUserProperties({
            anonymous_user_id,
          });
          updateUserMetadataAnalyticsParams();

          dispatch(setAuthAction('signIn'));
          await signInUser(signInResult.user as User);
        } else {
          dispatch(setIsLinkUserExist(true));
        }
      } catch (catchError) {
        handleAuthError(catchError, { action: 'handleUserAlreadyExistWithDifferentCredentials' });
      }
    },
    [signInWithCustomToken]
  );

  const trackRegistrationEvents = useCallback(
    (userResponse: UserType, type: AuthProvidersEnum) => {
      trackPredefinedEvent('client_sign_up', {
        clientId: userResponse?.id,
      });

      trackPredefinedEvent('USER_REGISTRATION', {
        registration_method: type,
        $user_id: userResponse.id,
        $first_name: userResponse?.display_name,
        $last_name: userResponse?.last_name,
        $email: userResponse?.email,
        ...(newOnBoardingData
          ? {
              funnel_id: newOnBoardingData.id,
              funnel_name: newOnBoardingData.name,
              funnel_type: newOnBoardingData.type,
              ...(newOnBoardingData?.experiment && {
                onboarding_experiment_name: newOnBoardingData?.experiment,
              }),
            }
          : { funnel_type: 'default' }),
      });

      trackPredefinedEvent('CompleteRegistration', {
        eventInstanceId: userResponse?.id,
        userId: userResponse?.id,
        ...(userResponse?.email && { $email: userResponse?.email }),
        ...(userResponse?.display_name && { $first_name: userResponse?.display_name }),
        ...(userResponse?.last_name && { $last_name: userResponse?.last_name }),
      });
    },
    [newOnBoardingData, trackPredefinedEvent]
  );

  const createNewUser = useCallback(
    async (authLocalUser: User, type: AuthProvidersEnum, isNeedAdditionalData?: boolean) => {
      dispatch(setAuthAction('signUp'));

      const userResponse = await dispatch(createUserProfile({ authUser: authLocalUser })).unwrap();

      localStorage.removeItem('onboardingToken');
      localStorage.removeItem('webToWebAuth');

      trackRegistrationEvents(userResponse, type);

      // Navigate to personal data update if needed, otherwise continue to main flow
      if (isNeedAdditionalData || !userResponse?.dob || !userResponse?.country) {
        dispatch(updateSignUpData({ isNeedAdditionalData: true }));

        history.push(PrivateRoutes.NEED_UPDATE_PERSONAL_DATA);
        return;
      }

      const isRedirectToOnboarding = await redirectToOnboarding(userResponse);
      if (isRedirectToOnboarding) return;

      history.push(redirectTo ? redirectTo : PrivateRoutes.PSYCHOLOGIST_LIST);
      dispatch(resetAuthState());
    },
    [trackRegistrationEvents, redirectToOnboarding, dispatch, history, redirectTo, handleAuthError]
  );

  const signInUser = useCallback(
    async (authLocalUser: User, isNeedAdditionalData?: boolean) => {
      const databaseUser = await dispatch(updateUserProfile({ authUser: authLocalUser })).unwrap();
      await dispatch(initChatRooms());

      localStorage.removeItem('onboardingToken');
      localStorage.removeItem('webToWebAuth');

      if (isNeedAdditionalData) {
        return history.push(PrivateRoutes.NEED_UPDATE_PERSONAL_DATA);
      }

      const [consultations] = await Promise.all([
        dispatch(loadConsultationsThunk(databaseUser.id)),
        dispatch(fetchSubscriptions(databaseUser.id)),
      ]);

      localStorage.removeItem('onboardingToken');
      sessionStorage.removeItem('newOnBoardingDataState');
      localStorage.removeItem('webToWebAuth');

      if (!databaseUser?.dob || !databaseUser?.country) {
        return history.push(PrivateRoutes.NEED_UPDATE_PERSONAL_DATA);
      }

      if (redirectTo) {
        history.push(redirectTo);
        return dispatch(resetAuthState());
      }

      const isRedirectToOnboarding = await redirectToOnboarding(databaseUser);
      if (isRedirectToOnboarding) return;

      const otherUserWithNewAnswers = databaseUser.id !== onBoardingUserId && newOnBoardingAnswers;
      if (otherUserWithNewAnswers) {
        clearSavedFilters(i18n.language, databaseUser.id);
      }

      history.push(
        consultations?.length && !otherUserWithNewAnswers ? PrivateRoutes.CONSULTATION : PrivateRoutes.PSYCHOLOGIST_LIST
      );
      dispatch(resetAuthState());
    },
    [
      dispatch,
      history,
      redirectTo,
      redirectToOnboarding,
      onBoardingUserId,
      newOnBoardingAnswers,
      handleAuthError,
      i18n.language,
    ]
  );

  const linkUser = useCallback(
    async (authLocalUser: User, type: AuthProvidersEnum, isNeedAdditionalData?: boolean) => {
      const providerData = authLocalUser.providerData?.find(
        (provider) => provider.providerId === ProvderToProviderIdMatch[type as keyof typeof ProvderToProviderIdMatch]
      );
      await FirebaseAuthentication.updateProfile({
        displayName: providerData?.displayName ?? undefined,
        photoUrl: providerData?.photoUrl ?? undefined,
      });
      const updatedProfileResult = await FirebaseAuthentication.getCurrentUser();

      const result = await dispatch(linkUserProfile({ authUser: updatedProfileResult.user as User })).unwrap();
      authUser?.email && (await updateEmail({ newEmail: authUser.email }));

      trackRegistrationEvents(
        {
          id: authLocalUser?.uid,
          display_name: result?.display_name,
          email: result?.email,
          last_name: result?.last_name,
        } as UserType,
        type
      );

      dispatch(setAuthAction('signIn'));
      isNeedAdditionalData && dispatch(updateSignUpData({ isNeedAdditionalData: true }));
      await signInUser(authLocalUser, isNeedAdditionalData);
    },
    [dispatch, authUser, trackRegistrationEvents, handleAuthError]
  );

  const setPendingActionAndProvider = useCallback((action: 'signIn' | 'link', provider: AuthProvidersEnum) => {
    /**
     * On native platforms we don't use redirect method, so we don't need to set pending action and provider
     */
    if (isPlatform('hybrid')) {
      return;
    }

    sessionStorage.setItem('pendingAction', action);
    sessionStorage.setItem('pendingProvider', provider);
  }, []);

  /**
   * SIGN OUT FLOWS
   */
  const triggerSignOutFlow = useCallback(async () => {
    try {
      await signOut();
      reset();
      clearSavedSort();
      await clearUser(i18n.language);
      dispatch(logoutAction());
    } catch (error) {
      handleAuthError(error, {
        action: 'triggerSignOutFlow',
      });
      throw error;
    }
  }, [signOut, dispatch, logoutAction, handleAuthError]);

  /**
   * SIGN IN FLOWS
   */
  const triggerSignInWithAppleFlow = useCallback(async () => {
    try {
      dispatch(setIsLinkUserExist(false));
      setPendingActionAndProvider('signIn', AuthProvidersEnum.APPLE);
      const result = await signInWithApple();

      /**
       * We need to check if the user is already in the database,
       * in cases when specialist try to sign in with email from specialist in client app
       **/
      const userResponse = await dispatch(loadUserThunk(result?.user?.uid ?? '')).unwrap();

      /**
       * On native platforms we don't use redirect method, so we need to handle the user sign up or sign in
       * !!! Next lines of code is only executed on native platforms, on web platforms we use redirect method.
       */
      if (!userResponse) {
        await createNewUser(result.user as User, AuthProvidersEnum.APPLE, true);
      } else {
        await signInUser(result.user as User);
      }

      return result;
    } catch (error: any) {
      handleAuthError(error, { action: 'signInWithApple', provider: 'apple' });
      if (error?.code !== 'auth/popup-closed-by-user' && !error?.errorMessage?.includes('1001')) {
        throw error;
      }
    }
  }, [signInWithApple, dispatch, setAuthAction, updateSignUpData, createNewUser, signInUser, handleAuthError]);

  const triggerSignInWithGoogleFlow = useCallback(async () => {
    try {
      dispatch(setIsLinkUserExist(false));
      setPendingActionAndProvider('signIn', AuthProvidersEnum.GOOGLE);
      const result = await signInWithGoogle();

      /**
       * On native platforms we don't use redirect method, so we need to handle the user sign up or sign in
       * !!! Next lines of code is only executed on native platforms, on web platforms we use redirect method.
       */

      /**
       * We need to check if the user is already in the database,
       * in cases when specialist try to sign in with email from specialist in client app
       **/
      const userResponse = await dispatch(loadUserThunk(result?.user?.uid ?? '')).unwrap();

      if (!userResponse) {
        await createNewUser(result.user as User, AuthProvidersEnum.GOOGLE, true);
      } else {
        await signInUser(result.user as User);
      }

      return result;
    } catch (error: any) {
      handleAuthError(error, { action: 'signInWithGoogle', provider: 'google' });

      if (
        error?.code !== 'auth/popup-closed-by-user' &&
        !error?.errorMessage?.includes('The user canceled the sign-in') &&
        !error?.toString().includes('User cancelled the selector') &&
        error?.toString() !== 'Error: 12501: '
      ) {
        throw error;
      }
    }
  }, [signInWithGoogle, dispatch, setAuthAction, updateSignUpData, createNewUser, signInUser, handleAuthError]);

  const triggerSignInWithEmailAndPasswordFlow = useCallback(
    async (options: SignInWithEmailAndPasswordOptions) => {
      try {
        const result = await signInWithEmailAndPassword(options);
        /**
         * We need to check if the user is already in the database,
         * in cases when specialist try to sign in with email from specialist in client app
         **/
        const userResponse = await dispatch(loadUserThunk(result?.user?.uid ?? '')).unwrap();

        if (!userResponse) {
          await createNewUser(result.user as User, AuthProvidersEnum.EMAIL, true);
        } else {
          await signInUser(result.user as User);
        }

        return result;
      } catch (error) {
        handleAuthError(error, {
          action: 'signInWithEmailAndPassword',
          provider: 'email',
          email: options.email?.substring(0, 3) + '***', // Partial email for context
        });

        throw error;
      }
    },
    [signInWithEmailAndPassword, createNewUser, signInUser, handleAuthError]
  );

  const triggerSignInAnonymouslyFlow = useCallback(async () => {
    try {
      const result = await signInAnonymously();
      return result;
    } catch (error) {
      handleAuthError(error, { action: 'signInAnonymously' });
      throw error;
    }
  }, [signInAnonymously, handleAuthError]);

  const triggerSignInWithCustomTokenFlow = useCallback(
    async (token: string) => {
      try {
        const result = await signInWithCustomToken(token);
        return result;
      } catch (error) {
        await triggerSignOutFlow();
        throw error;
      }
    },
    [signInWithCustomToken, triggerSignOutFlow, handleAuthError]
  );

  /**
   * SIGN UP FLOWS
   */
  const triggerCreateUserWithEmailAndPasswordFlow = useCallback(
    async (options: CreateUserWithEmailAndPasswordOptions) => {
      try {
        const result = await createUserWithEmailAndPassword(options);
        await createNewUser(result.user as User, AuthProvidersEnum.EMAIL);

        return result;
      } catch (error) {
        handleAuthError(error, { action: 'triggerCreateUserWithEmailAndPasswordFlow' });
        throw error;
      }
    },
    [createUserWithEmailAndPassword, createNewUser, handleAuthError]
  );

  const triggerCreateOrLinkUserWithRedirectResultFlow = useCallback(async () => {
    const pendingProvider = sessionStorage.getItem('pendingProvider') as AuthProvidersEnum;
    const pendingAction = sessionStorage.getItem('pendingAction');

    sessionStorage.removeItem('pendingProvider');
    sessionStorage.removeItem('pendingAction');

    try {
      // Move to ui lib
      const redirectResult = await FirebaseAuthentication.getRedirectResult();
      if (pendingAction && (!redirectResult || !redirectResult.user)) {
        throw new Error('No redirect result found');
      }
      if (!redirectResult || !redirectResult.user) {
        return;
      }
      const userResponse = await dispatch(loadUserThunk(redirectResult?.user?.uid ?? '')).unwrap();

      // handle user sign up in application with redirect result
      if (pendingAction === 'signIn' && !userResponse) {
        await createNewUser(redirectResult.user as User, pendingProvider, true);
      }

      if (pendingAction === 'link') {
        await linkUser(redirectResult.user as User, pendingProvider, true);
      }

      return;
    } catch (error: any) {
      if (pendingAction === 'link' && checkIfErrorIsUserAlreadyExist(error)) {
        await handleUserAlreadyExistWithDifferentCredentials(error);
      } else {
        handleAuthError(error, { action: 'triggerCreateOrLinkUserWithRedirectResultFlow' });
        throw error;
      }
    }
  }, [createNewUser]);

  /**
   * LINK USER FLOWS
   */
  const triggerLinkWithGoogleFlow = useCallback(async () => {
    try {
      setPendingActionAndProvider('link', AuthProvidersEnum.GOOGLE);
      const result = await linkWithGoogle();

      /**
       * On native platforms we don't use redirect method, so we need to handle the user sign up or sign in
       * !!! Next lines of code is only executed on native platforms, on web platforms we use redirect method.
       */
      await linkUser(result.user as User, AuthProvidersEnum.GOOGLE, true);

      return result;
    } catch (error: any) {
      if (checkIfErrorIsUserAlreadyExist(error)) {
        await handleUserAlreadyExistWithDifferentCredentials(error);
      } else if (
        error?.code !== 'auth/popup-closed-by-user' &&
        !error?.errorMessage?.includes('The user canceled the sign-in') &&
        error?.toString() !== 'Error: 12501: '
      ) {
        handleAuthError(error, { action: 'triggerLinkWithGoogleFlow' });
        throw error;
      }
    }
  }, [
    setPendingActionAndProvider,
    linkWithGoogle,
    dispatch,
    updateSignUpData,
    linkUser,
    setAuthAction,
    signInUser,
    handleAuthError,
    handleUserAlreadyExistWithDifferentCredentials,
  ]);

  const triggerLinkWithAppleFlow = useCallback(async () => {
    try {
      setPendingActionAndProvider('link', AuthProvidersEnum.APPLE);
      const result = await linkWithApple();

      /**
       * On native platforms we don't use redirect method, so we need to handle the user sign up or sign in
       * !!! Next lines of code is only executed on native platforms, on web platforms we use redirect method.
       */
      await linkUser(result.user as User, AuthProvidersEnum.APPLE, true);

      return result;
    } catch (error: any) {
      if (checkIfErrorIsUserAlreadyExist(error)) {
        await handleUserAlreadyExistWithDifferentCredentials(error);
      } else if (error?.code !== 'auth/popup-closed-by-user' && !error?.errorMessage?.includes('1001')) {
        handleAuthError(error, { action: 'triggerLinkWithAppleFlow' });
        throw error;
      }
    }
  }, [
    setPendingActionAndProvider,
    linkWithApple,
    dispatch,
    updateSignUpData,
    linkUser,
    setAuthAction,
    signInUser,
    handleAuthError,
    handleUserAlreadyExistWithDifferentCredentials,
  ]);

  const triggerLinkWithEmailAndPasswordFlow = useCallback(
    async (options: LinkWithEmailAndPasswordOptions) => {
      try {
        const result = await linkWithEmailAndPassword(options);

        await linkUser(result.user as User, AuthProvidersEnum.EMAIL);

        return result;
      } catch (error) {
        handleAuthError(error, { action: 'triggerLinkWithEmailAndPasswordFlow' });
        throw error;
      }
    },
    [linkWithEmailAndPassword, linkUser, signInUser, handleAuthError]
  );

  /**
   * TECHNICAL AUTH FUNCTIONS
   */
  const updateEmail = useCallback(
    async (options: UpdateEmailOptions) => {
      try {
        const result = await updateUserEmail(options.newEmail);

        return result;
      } catch (error) {
        handleAuthError(error, { action: 'updateEmail' });
        throw error;
      }
    },
    [handleAuthError]
  );

  const deleteUser = useCallback(async () => {
    try {
      const result = await removeUserApi();

      return result;
    } catch (error) {
      handleAuthError(error, { action: 'deleteUser' });
      throw error;
    }
  }, [handleAuthError]);

  const isEmailExist = useCallback(
    async (email: string) => {
      try {
        const response = await checkIsEmailExist(email);

        if (response.error) {
          throw new Error(response.error?.[0].msg);
        }

        return response.data;
      } catch (error: any) {
        handleAuthError(error, { action: 'isEmailExist' });
        throw error;
      }
    },
    [handleAuthError]
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      try {
        await changePassword(newPassword);
      } catch (error) {
        handleAuthError(error, { action: 'updatePassword' });
        throw error;
      }
    },
    [changePassword, handleAuthError]
  );

  const sendPasswordResetEmailToUser = useCallback(
    async (options: SendPasswordResetEmailOptions) => {
      try {
        await sendPasswordResetEmail(options);
      } catch (error) {
        handleAuthError(error, { action: 'sendPasswordResetEmailToUser' });
        throw error;
      }
    },
    [sendPasswordResetEmail, handleAuthError]
  );

  return {
    // sign out flows
    triggerSignOutFlow,

    // sign in flows
    triggerSignInWithAppleFlow,
    triggerSignInWithGoogleFlow,
    triggerSignInAnonymouslyFlow,
    triggerSignInWithCustomTokenFlow,
    triggerSignInWithEmailAndPasswordFlow,

    // sign up flows
    triggerCreateOrLinkUserWithRedirectResultFlow,
    triggerCreateUserWithEmailAndPasswordFlow,

    // link user flows
    linkUser,
    triggerLinkWithGoogleFlow,
    triggerLinkWithAppleFlow,
    triggerLinkWithEmailAndPasswordFlow,

    // technical auth functions
    updateEmail,
    deleteUser,
    isEmailExist,
    updatePassword,
    sendPasswordResetEmailToUser,

    // auth context
    isAuthLoading,
    isAuthReady,
    authUser,
  };
};
